import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPagosCobros, resetPagosCobros } from "redux/actions/pagoCobrosAction";
import { fetchModuloContable } from "redux/actions/contableActions";
import { getComprobantesModuloContableAction } from "redux/actions/contableComprobantesAction";
import { getRUCEmpresa } from "redux/actions/userActions";

const DEFAULT_PAGE_SIZE = 15;

const usePagosCobrosView = () => {
  const dispatch = useDispatch();
  const tokenUser = useSelector((state) => state.user.tk);
  const pagoCobros = useSelector((state) => state.pagoCobros);
  const dashboard = useSelector((state) => state.contable.dashboard);
  const authorizedVouchers = useSelector((state) => state.contableComprobantes);

  const today = new Date();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [mes, setMes] = useState(today.getMonth() + 1);
  const [anio, setAnio] = useState(today.getFullYear());

  /* Stats: se combinan dos fuentes, cada una calcula solo una parte -
     - pago-cobro: cobradoValor/Cantidad, porCobrarValor/Cantidad
     - modulo/contable: ingresosMes, gastos, utilidadNeta, notas de credito
     Sin las dos llamadas, esos campos quedan en 0 (ver stats mas abajo). */
  useEffect(() => {
    if (!tokenUser) return;
    const ruc = getRUCEmpresa(tokenUser);
    dispatch(fetchPagosCobros(ruc, anio, mes)).catch(() => {});
    dispatch(fetchModuloContable(ruc, anio, mes)).catch(() => {});
  }, [dispatch, tokenUser, anio, mes]);

  const stats = useMemo(() => {
    if (!pagoCobros.data && !dashboard.data) return null;
    return {
      ...dashboard.data,
      cobradoValor: pagoCobros.data?.cobradoValor,
      cobradoCantidad: pagoCobros.data?.cobradoCantidad,
      porCobrarValor: pagoCobros.data?.porCobrarValor,
      porCobrarCantidad: pagoCobros.data?.porCobrarCantidad,
    };
  }, [pagoCobros.data, dashboard.data]);

  /* Limpieza al desmontar */
  useEffect(() => {
    return () => { dispatch(resetPagosCobros()); };
  }, [dispatch]);

  /* Tabla: comprobantes autorizados (endpoint nuevo del modulo contable) */
  const fetchVouchers = useCallback(() => {
    if (!tokenUser) return;
    dispatch(
      getComprobantesModuloContableAction({
        rucEmpresa: tokenUser,
        page,
        elementos: rowsPerPage,
        anio,
        mes,
        type: null,
      }),
    );
  }, [dispatch, tokenUser, page, rowsPerPage, anio, mes]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  const handlePageChange = (_e, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleMesChange = useCallback((value) => {
    setMes(value);
    setPage(0);
  }, []);
  const handleAnioChange = useCallback((value) => {
    setAnio(value);
    setPage(0);
  }, []);

  return {
    stats,
    statsLoading: pagoCobros.loading || dashboard.loading,
    statsError: pagoCobros.error || dashboard.error,
    authorizedVouchers,
    page,
    rowsPerPage,
    onPageChange: handlePageChange,
    onRowsPerPageChange: handleRowsPerPageChange,
    reload: fetchVouchers,
    mes,
    anio,
    onMesChange: handleMesChange,
    onAnioChange: handleAnioChange,
  };
};

export default usePagosCobrosView;
